import { useState, useEffect } from "react";

/**
 * Device Status Hook
 * Provides battery level, charging status, and network connection type
 * Uses the Battery Status API and Network Information API
 */

export interface DeviceStatus {
  batteryLevel: number | null; // 0-100 or null if unavailable
  isCharging: boolean | null; // true/false or null if unavailable
  networkType: string | null; // "wifi", "cellular", etc. or null if unavailable
}

// Extend Navigator interface to include non-standard APIs
interface NavigatorWithBattery extends Navigator {
  getBattery?: () => Promise<BatteryManager>;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation;
  mozConnection?: NetworkInformation;
  webkitConnection?: NetworkInformation;
}

interface BatteryManager extends EventTarget {
  charging: boolean;
  chargingTime: number;
  dischargingTime: number;
  level: number;
  onchargingchange: ((this: BatteryManager, ev: Event) => any) | null;
  onchargingtimechange: ((this: BatteryManager, ev: Event) => any) | null;
  ondischargingtimechange: ((this: BatteryManager, ev: Event) => any) | null;
  onlevelchange: ((this: BatteryManager, ev: Event) => any) | null;
}

interface NetworkInformation extends EventTarget {
  type?:
    | "bluetooth"
    | "cellular"
    | "ethernet"
    | "none"
    | "wifi"
    | "wimax"
    | "other"
    | "unknown";
  effectiveType?: "slow-2g" | "2g" | "3g" | "4g";
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  onchange?: ((this: NetworkInformation, ev: Event) => any) | null;
}

export function useDeviceStatus(): DeviceStatus {
  const [batteryLevel, setBatteryLevel] = useState<number | null>(null);
  const [isCharging, setIsCharging] = useState<boolean | null>(null);
  const [networkType, setNetworkType] = useState<string | null>(null);

  // Battery Status API
  useEffect(() => {
    const nav = navigator as NavigatorWithBattery;

    if (!nav.getBattery) {
      console.log("[DeviceStatus] Battery API not supported");
      return;
    }

    let cleanupFns: (() => void)[] = [];

    const updateBatteryInfo = (batt: BatteryManager) => {
      setBatteryLevel(Math.round(batt.level * 100));
      setIsCharging(batt.charging);
    };

    nav
      .getBattery()
      .then((batt) => {
        updateBatteryInfo(batt);

        // Listen for changes
        const handleLevelChange = () => updateBatteryInfo(batt);
        const handleChargingChange = () => updateBatteryInfo(batt);

        batt.addEventListener("levelchange", handleLevelChange);
        batt.addEventListener("chargingchange", handleChargingChange);

        cleanupFns.push(() => {
          batt.removeEventListener("levelchange", handleLevelChange);
          batt.removeEventListener("chargingchange", handleChargingChange);
        });
      })
      .catch((err) => {
        console.warn("[DeviceStatus] Failed to get battery info:", err);
      });

    return () => {
      cleanupFns.forEach((fn) => fn());
    };
  }, []);

  // Network Information API
  useEffect(() => {
    const nav = navigator as NavigatorWithConnection;
    const connection =
      nav.connection || nav.mozConnection || nav.webkitConnection;

    if (!connection) {
      console.log("[DeviceStatus] Network Information API not supported");
      return;
    }

    const updateNetworkInfo = () => {
      // Use `type` if available, otherwise fall back to `effectiveType`
      const type = connection.type || connection.effectiveType || null;
      setNetworkType(type);
    };

    updateNetworkInfo();

    // Listen for changes
    if (connection.onchange !== undefined) {
      connection.addEventListener("change", updateNetworkInfo);
      return () => {
        connection.removeEventListener("change", updateNetworkInfo);
      };
    }
  }, []);

  return {
    batteryLevel,
    isCharging,
    networkType,
  };
}
